/**
 * Modal de seleção de qualidade.
 * Lista todas as qualidades disponíveis para o usuário escolher.
 * 
 * Segue padrões de UI do projeto:
 * - Usa componente Button do sistema
 * - Overlay padrão: bg-black/50 backdrop-blur-sm
 * - Container: bg-navy-800 border border-navy-700 rounded-lg
 * 
 * ✅ CORREÇÃO: Adicionado suporte a estado de erro, loading e retry
 */

import { useState, useEffect, useRef } from 'react';
import { X, Check, Star, Search, RefreshCw, AlertTriangle, ArrowRight } from 'lucide-react';
import { Button } from '../../../ui/Button';

export interface Quality {
  id: number;
  one1_nome: string;
  prod_catg_id: number;
}

interface QualitySelectModalProps {
  isOpen: boolean;
  qualities: Quality[];
  onSelect: (quality: Quality) => void;
  onClose: () => void;
  // ✅ NOVAS PROPS: Para tratamento de erro e retry
  error?: string | null;
  isLoading?: boolean;
  onRetry?: () => void;
  onSkip?: () => void; // Opção de pular quando não há qualidades
}

export function QualitySelectModal({
  isOpen,
  qualities,
  onSelect,
  onClose,
  error = null,
  isLoading = false,
  onRetry,
  onSkip
}: QualitySelectModalProps) {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  // ✅ CORREÇÃO: Estado para controlar quando mostrar retry (após 5 segundos)
  const [showRetry, setShowRetry] = useState(false);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // ✅ CORREÇÃO: Mostrar retry só depois de 5 segundos de erro
  useEffect(() => {
    if (error && !isLoading) {
      // Limpar timeout anterior se existir
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }

      // Aguardar 5 segundos antes de mostrar retry
      retryTimeoutRef.current = setTimeout(() => {
        setShowRetry(true);
      }, 5000);
    } else {
      setShowRetry(false);
    }

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [error, isLoading]);

  if (!isOpen) return null;

  // Filtrar qualidades pela busca
  const filteredQualities = qualities.filter(quality => {
    const nome = quality.one1_nome?.toLowerCase() ?? '';
    const search = searchTerm.toLowerCase();
    return nome.includes(search);
  });

  const handleConfirm = () => {
    const selectedQuality = qualities.find(q => q.id === selectedId);
    if (selectedQuality) {
      onSelect(selectedQuality);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-navy-800 border border-navy-700 rounded-lg w-full max-w-[400px] max-h-[70vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-navy-700 shrink-0">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-purple-500/20 rounded-lg">
              <Star className="w-4 h-4 text-purple-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Selecionar Qualidade</h3>
              <p className="text-xs text-gray-400">Escolha a qualidade do material</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-navy-700 rounded transition-colors"
            aria-label="Fechar"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Search */}
        <div className="p-3 border-b border-navy-700 shrink-0">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar qualidade..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-3 py-2 bg-navy-900 border border-navy-600 rounded text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500"
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-2 min-h-0">
          {/* ✅ Estado de Loading */}
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <RefreshCw className="w-8 h-8 text-purple-400 animate-spin" />
              <p className="text-sm text-gray-400">Carregando qualidades...</p>
            </div>
          ) : error ? (
            /* ✅ Estado de Erro com Retry (só após 5 segundos) */
            <div className="flex flex-col items-center justify-center py-6 gap-4">
              <div className="p-3 bg-red-500/20 rounded-full">
                <AlertTriangle className="w-8 h-8 text-red-400" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-red-400 mb-1">Erro ao carregar qualidades</p>
                <p className="text-xs text-gray-500 max-w-[250px]">{error}</p>
                {!showRetry && (
                  <p className="text-xs text-gray-600 mt-2">Aguarde alguns segundos...</p>
                )}
              </div>
              {/* ✅ CORREÇÃO: Só mostrar retry depois de 5 segundos */}
              {showRetry && onRetry && (
                <div className="w-full px-4">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={onRetry}
                    className="w-full"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Tentar Novamente
                  </Button>
                </div>
              )}
            </div>
          ) : filteredQualities.length === 0 ? (
            /* ✅ Quando não há qualidades, mostrar mensagem e opção de retry */
            <div className="flex flex-col items-center justify-center py-6 gap-4">
              <div className="text-center">
                <p className="text-sm font-medium text-yellow-400 mb-2">
                  {searchTerm ? 'Nenhuma qualidade encontrada' : 'Nenhuma qualidade disponível'}
                </p>
                <p className="text-xs text-gray-500 max-w-[280px]">
                  Este produto não possui qualidades configuradas. É necessário selecionar uma qualidade para calcular o orçamento.
                </p>
              </div>
              <div className="flex flex-col gap-2 w-full px-4">
                {onRetry && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={onRetry}
                    className="w-full"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Tentar Carregar Novamente
                  </Button>
                )}
                {onSkip && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onSkip}
                    className="w-full text-gray-400 hover:text-white"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Recarregar Qualidades
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredQualities.map((quality) => {
                const isSelected = selectedId === quality.id;
                return (
                  <button
                    key={quality.id}
                    onClick={() => setSelectedId(quality.id)}
                    className={`w-full p-2.5 rounded border transition-all text-left ${isSelected
                        ? 'bg-purple-500/20 border-purple-500/50'
                        : 'bg-navy-700/50 border-navy-600 hover:bg-navy-700 hover:border-navy-500'
                      }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${isSelected
                              ? 'border-purple-400 bg-purple-500/20'
                              : 'border-gray-500'
                            }`}
                        >
                          {isSelected && (
                            <Check className="w-3 h-3 text-purple-400" />
                          )}
                        </div>
                        <span className="text-sm font-medium text-white">
                          {quality.one1_nome}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-3 border-t border-navy-700 shrink-0">
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleConfirm}
            disabled={selectedId === null || isLoading || !!error}
          >
            Confirmar
          </Button>
        </div>
      </div>
    </div>
  );
}

